import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, HostBinding, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, output } from "@angular/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { merge, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { SimpleFieldMetaData } from "../../../layout-meta-data";
import { MetaDataUtils } from "../../../meta-data/meta-data-utils";
import { MessageResourceManager } from "../../../resources/message-resource-manager";
import { LabelBoxComponent } from "../core/base/label-box/label-box.component";
import { ExtNewAutocompleteTextBoxComponent } from "../core/ext-new-autocomplete-text-box/ext-new-autocomplete-text-box.component";
import { ExternalFieldComponent } from "../core/external-field/external-field.component";
import { ExternalViewModelInterface, MetaDataDescriptionsInterface, PropertyViewModelInterface } from "@nts/std/interfaces";
import { FieldTypes } from "@nts/std/types";
import { ViewReadyCoordinator } from "../core/view-ready-coordinator";

@UntilDestroy()
@Component({
    selector: 'nts-external-list',
    templateUrl: './external-list.component.html',
    styleUrls: ['./external-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        LabelBoxComponent,
        ExtNewAutocompleteTextBoxComponent
    ]
})
export class ExternalListComponent extends ExternalFieldComponent implements OnInit, OnChanges, AfterViewInit {

    /**
     * Property valorizzata solo dal layout custom, vincerà sul externalPropertyViewModel.isVisible solo se sarà impostata a false
     */
    @Input() override isHidden = false;

    @HostBinding('class.additional-field')
    @Input() override additionalField = false;

    @HostBinding('class.full-column')
    @Input() fullColumn = false;

    @Input() externalPropertyViewModel: ExternalViewModelInterface;

    /**
     * @deprecated
     * Utilizza [decodeProperties] per elencare la lista delle decode properties
     */
    @Input() decodeDescription?: PropertyViewModelInterface;

    /**
     * @deprecated
     * Aggiungi in [decodeProperties] il codice se vuoi visualizzarlo
     */
    @Input() showCodeInDescription: boolean | null = null;

    /**
     * @deprecated
     * Il codice viene automaticamente recuperato dai metadati
     */
    @Input() code?: PropertyViewModelInterface | null;

    /**
     * Lista dei campi in Pascal Case, si possono anche passare i custom property view model (sempre in Pascal Case)
     */
    @Input() decodeProperties: string[] = null;

    /**
     * Lista dei campi dove effettuare la ricerca in Pascal Case
     */
    @Input() searchProperties: string[] = null;

    /**
     * Stringa da utilizzare come separatore
     */
    @Input() fieldSeparator: string = null;

    /**
     * Al momento supporta solo la stringa 'top', permette di impostare la label sopra il campo
     */
    @Input() labelPosition = '';

    @Input() isDisabled: boolean = false;
    @Input() tabIndex: number;
    @Input() isLabelZoomEnabled = true;
    @Input() primaryColor = null;
    @Input() appendTo = null;

    @Input() placeHolder: string = null;

    /**
     * Property per ridefinire il path per il layout.
     */
    @Input() customPathForLayout: string = null;

    @Output() externalPropertyViewModelInitialized = new EventEmitter<void>();
    readonly viewReady = output<void>();


    isViewReady = false;
    private _textBox: ExtNewAutocompleteTextBoxComponent | null = null;

    @ViewChild('textBox', { static: false })
    set textBox(component: ExtNewAutocompleteTextBoxComponent | null) {
        this._textBox = component;
        this.tryEmitViewReady();
    }
    get textBox(): ExtNewAutocompleteTextBoxComponent | null {
        return this._textBox;
    }

    /**
     * Property calcolata in base alle due property: isHidden e externalPropertyViewModel.isVisible
     */
    @HostBinding('class.is-hidden')
    isHiddenCalculated = false;

    override type = FieldTypes.ExternalList;
    private readonly viewReadyCoordinator = new ViewReadyCoordinator(['self', 'child'] as const, () => this.emitViewReady());

    get isFullWidth() {
        return (this.labelPosition === 'top' ? true : false);
    }

    get name(): string {
        return MetaDataUtils.toCamelCase(this.externalPropertyViewModel.externalMetaData.principalPropertyName);
    }

    get path(): string {
        if (this.customPathForLayout != null) {
            return this.customPathForLayout;
        }
        if (this.externalPropertyViewModel.customPathForLayout != null) {
            return this.externalPropertyViewModel.customPathForLayout;
        }
        return this.externalPropertyViewModel.reservedPath;
    }

    get description(): string {
        return this.externalPropertyViewModel.externalMetaData.descriptions.description?.length > 0 ? this.externalPropertyViewModel.externalMetaData.descriptions.description : MessageResourceManager.Current.getMessage(this.externalPropertyViewModel.externalMetaData.descriptions.descriptionKey);
    }

    get displayName(): string {
        return this.externalPropertyViewModel.externalMetaData.descriptions.displayName?.length > 0 ? this.externalPropertyViewModel.externalMetaData.descriptions.displayName : MessageResourceManager.Current.getMessage(this.externalPropertyViewModel.externalMetaData.descriptions.displayNameKey);
    }

    /**
     * Popola le descrizioni mancanti con i valori recuperati dalle risorse
     *
     * @param descriptions
     * @returns
     */
    getResolvedDescriptions(descriptions: MetaDataDescriptionsInterface) {
        return {
            displayNameKey: descriptions.displayNameKey,
            displayName: descriptions.displayName?.length > 0 ? descriptions.displayName : MessageResourceManager.Current.getMessage(descriptions.displayNameKey),
            shortNameKey: descriptions.shortNameKey,
            shortName: descriptions.shortName?.length > 0 ? descriptions.shortName : MessageResourceManager.Current.getMessage(descriptions.shortNameKey),
            descriptionKey: descriptions.descriptionKey,
            description: descriptions.description?.length > 0 ? descriptions.description : MessageResourceManager.Current.getMessage(descriptions.descriptionKey),
        };
    }

    get availableExternalFields(): SimpleFieldMetaData[] {
        return this.externalPropertyViewModel.externalMetaData.dependentAggregateMetaData.rootMetaData.propertyNames.map((p) => {
            const metaData = this.externalPropertyViewModel.externalMetaData.dependentAggregateMetaData.rootMetaData.getPropertyMetaData(p);
            if (metaData != null) {
                const field = new SimpleFieldMetaData();
                field.descriptions = this.getResolvedDescriptions(metaData.descriptions);
                field.name = metaData.name;
                return field;
            } else {
                console.error(`metadato di ${p} non trobvato per generare gli ExternalFields di ${this.externalPropertyViewModel.externalMetaData.dependentAggregateMetaData.rootMetaData.fullName}`);
            }
            return null;

        }).filter((f) => f != null);
    }

    protected destroyOnChange$: Subject<void> = new Subject<void>();

    constructor(private cd: ChangeDetectorRef) {
        super();
    }

    ngOnInit() {
        if (!this.externalPropertyViewModel) { throw new Error('Missing viewModel for externalPropertyViewModel!'); }
        let decodeProperties: string[] = null;
        if (this.decodeDescription) {

            if (this.decodeDescription?.isCustom === false) {
                decodeProperties = [this.decodeDescription.propertyMetaData.name];
            } else {
                decodeProperties = [this.decodeDescription.propertyName];
            }

        }
        if (this.decodeProperties?.length > 0) {
            decodeProperties = this.decodeProperties;
        }
        if (this.showCodeInDescription == null) {
            this.showCodeInDescription = this.externalPropertyViewModel.showCode;
        }
        this.decodeProperties = decodeProperties;
    }

    ngAfterViewInit(): void {
        queueMicrotask(() => {
            this.viewReadyCoordinator.markReady('self');
            this.tryEmitViewReady();
        });
    }

    ngOnChanges(changes: SimpleChanges) {

        if (changes['isHidden']) {
            if (changes['isHidden']?.currentValue === true) {
                this.isHiddenCalculated = true;
            } else {
                this.isHiddenCalculated = !this.externalPropertyViewModel?.isVisible;
            }
        } else if (changes['externalPropertyViewModel']) {
            this.destroyOnChange$.next();

            this.isHiddenCalculated = this.isHidden === true ? true : !this.externalPropertyViewModel.isVisible;

            this.externalPropertyViewModel?.componentIstance$.next(this);

            merge(
                this.externalPropertyViewModel.externalDomainModelChanged,
                this.externalPropertyViewModel.decodeCompleted,
                this.externalPropertyViewModel.propertyChanged
            ).pipe(
                untilDestroyed(this),
                takeUntil(this.destroyOnChange$)
            ).subscribe(() => {
                this.cd.detectChanges();
            });

            this.externalPropertyViewModelInitialized.emit();
        }
    }

    onTextBoxViewReady(): void {
        this.viewReadyCoordinator.markReady('child');
        this.tryEmitViewReady();
    }

    private tryEmitViewReady(): void {
        this.viewReadyCoordinator.tryEmit(() => this.textBox != null);
    }

    getCodes(): string[] | null {
        return this.externalPropertyViewModel?.domainModelMetaData?.identityNames?.map((i) => MetaDataUtils.toCamelCase(i)) ?? null;
    }

    getCodesPvm(): PropertyViewModelInterface[] | null {
        return this.getCodes()?.map((c) => this.externalPropertyViewModel.getProperty(c)) ?? null;
    }

    async openZoom() {
        if ((this.isDisabled || this.externalPropertyViewModel.isEnabled === false || !this.getCodesPvm()?.every((c) => c.isEnabled)) && this.externalPropertyViewModel?.securityAccess == null) {
            this.textBox.startPresentation(null, true, false);
            return;
        }
        if (this.externalPropertyViewModel?.securityAccess == null) {
            await this.textBox.zoom();
        }
    }

    private emitViewReady(): void {
        this.isViewReady = true;
        this.viewReady.emit();
    }
}
